import FirebaseService from './firebase-service.js';

class ProjectManager {
  constructor(engine) {
    this.engine = engine;
    this.firebaseService = new FirebaseService();
    this.currentProjectId = null;
    this.currentProjectName = null;
    
    // Inizializza Firebase
    this.firebaseService.init();
  }

  // ========== SERIALIZZAZIONE ==========

  serializeProject() {
    const furnitureController = this.engine.getFurnitureController();
    const wallController = this.engine.getWallController();
    const doorController = this.engine.getDoorController();
    const windowController = this.engine.getWindowController();

    // Serializza mobili
    const furniture = furnitureController.getAllFurniture().map(f => {
      const serialized = f.serialize();
      
      // Se è un modello custom, aggiungi informazioni aggiuntive
      if (f.catalogItem.isCustom) {
        serialized.isCustomModel = true;
        serialized.customModelId = f.catalogItem.id;
        serialized.customModelName = f.catalogItem.name;
      }
      
      return serialized;
    });

    // Serializza pareti
    const walls = wallController.getAllWalls().map(w => ({
      id: w.id,
      start: { x: w.start.x, y: w.start.y, z: w.start.z },
      end: { x: w.end.x, y: w.end.y, z: w.end.z },
      height: w.height,
      thickness: w.thickness
    }));

    // Serializza porte
    const doors = doorController.getAllDoors().map(d => ({
      id: d.id,
      wallId: d.wall.id,
      positionOnWall: d.positionOnWall,
      width: d.width,
      height: d.height,
      doorType: d.doorType ?? 'swing',
      isFlipped: d.isFlipped
    }));

    // Serializza finestre
    const windows = windowController.getAllWindows().map(w => ({
      id: w.id,
      wallId: w.wall.id,
      positionOnWall: w.positionOnWall,
      width: w.width,
      height: w.height,
      sillHeight: w.sillHeight,
      isFlipped: w.isFlipped
    }));

    // Raccoglie metadati modelli custom
    const customModelsMetadata = this.getCustomModelsMetadata();

    return {
      version: '1.0',
      projectLocked: window.app?.isLocked ?? false,
      furniture,
      walls,
      doors,
      windows,
      customModelsMetadata
    };
  }

  getCustomModelsMetadata() {
    const furnitureController = this.engine.getFurnitureController();
    const customModelLoader = furnitureController.customModelLoader;
    
    if (!customModelLoader) return [];

    // Raccoglie tutti i modelli custom USATI nella scena
    const usedCustomModels = new Map();
    
    furnitureController.getAllFurniture().forEach(furniture => {
      if (furniture.catalogItem.isCustom) {
        const modelId = furniture.catalogItem.id;
        
        // Se non è già aggiunto, salvalo
        if (!usedCustomModels.has(modelId)) {
          usedCustomModels.set(modelId, {
            id: furniture.catalogItem.id,
            name: furniture.catalogItem.name,
            dimensions: furniture.catalogItem.dimensions,
            snapToWall: furniture.catalogItem.snapToWall,
            snapToGrid: furniture.catalogItem.snapToGrid,
            rotationStep: furniture.catalogItem.rotationStep,
            thumbnail: furniture.catalogItem.thumbnail
          });
        }
      }
    });
    
    // Converte Map in array
    return Array.from(usedCustomModels.values());
  }

  // ========== DESERIALIZZAZIONE ==========

  async deserializeProject(projectData) {
    const furnitureController = this.engine.getFurnitureController();
    const wallController = this.engine.getWallController();
    const doorController = this.engine.getDoorController();
    const windowController = this.engine.getWindowController();

    // Pulisce scena esistente
    this.clearScene();

    // 1. Carica modelli custom se necessario
    await this.loadCustomModels(projectData.customModelsMetadata || []);

    // 2. Ricostruisce pareti
    const wallsMap = new Map();
    for (const wallData of projectData.walls) {
      const wall = new Wall(
        new THREE.Vector3(wallData.start.x, wallData.start.y, wallData.start.z),
        new THREE.Vector3(wallData.end.x, wallData.end.y, wallData.end.z),
        wallData.height,
        wallData.thickness
      );
      wall.id = wallData.id;
      wallController.addWall(wall);
      wallsMap.set(wall.id, wall);
    }

    // 3. Ricostruisce porte
    for (const doorData of projectData.doors) {
      const wall = wallsMap.get(doorData.wallId);
      if (wall) {
        const door = new Door(
          wall,
          doorData.positionOnWall,
          doorData.width,
          doorData.height,
          doorData.doorType ?? 'swing'
        );
        door.id = doorData.id;
        door.isFlipped = doorData.isFlipped || false;
        doorController.addDoor(door);
      }
    }

    // 4. Ricostruisce finestre
    for (const windowData of projectData.windows) {
      const wall = wallsMap.get(windowData.wallId);
      if (wall) {
        const window = new Window(
          wall,
          windowData.positionOnWall,
          windowData.width,
          windowData.height,
          windowData.sillHeight
        );
        window.id = windowData.id;
        window.isFlipped = windowData.isFlipped || false;
        windowController.addWindow(window);
      }
    }

    // 5. Ricostruisce mobili
    for (const furnitureData of projectData.furniture) {
      const catalogItem = furnitureController.getItemById(furnitureData.catalogItemId);

      if (catalogItem) {
        const position = new THREE.Vector3(
          furnitureData.position.x,
          furnitureData.position.y,
          furnitureData.position.z
        );

        const furniture = await furnitureController.createFurniture(
          catalogItem.id,
          position
        );

        if (furniture) {
          furniture.id = furnitureData.id;
          furniture.rotation = furnitureData.rotation;
          furniture.scale = furnitureData.scale;
          furniture.updateTransform();
        }
      } else {
        console.warn(`Modello "${furnitureData.catalogItemId}" non trovato. Ignorato.`);
      }
    }

    console.log('✓ Progetto deserializzato completamente');

    // Applica stato lucchetto salvato
    if (typeof projectData.projectLocked === 'boolean' && window.app) {
      window.app.setLocked(projectData.projectLocked);
    }
  }

  async loadCustomModels(customModelsMetadata) {
    const furnitureController = this.engine.getFurnitureController();
    const customModelLoader = furnitureController.customModelLoader;

    if (!customModelLoader || customModelsMetadata.length === 0) return;

    let modelsAdded = false;

    for (const metadata of customModelsMetadata) {
      // Verifica se il modello esiste già localmente
      const existingModel = customModelLoader.getCustomModels().find(m => m.id === metadata.id);
      
      if (!existingModel) {
        try {
          // Prova a scaricare da Firebase Storage
          const modelData = await this.firebaseService.downloadCustomModel(metadata.id);
          
          // Ricostruisce l'oggetto modello custom
          const customModel = {
            id: metadata.id,
            name: metadata.name,
            modelData: modelData,
            thumbnail: metadata.thumbnail,
            dimensions: metadata.dimensions,
            snapToWall: metadata.snapToWall,
            snapToGrid: metadata.snapToGrid,
            rotationStep: metadata.rotationStep,
            isCustom: true
          };

          customModelLoader.customModels.push(customModel);
          modelsAdded = true;
          console.log('✓ Modello custom scaricato da Firebase:', metadata.name);
        } catch (error) {
          console.warn(`Modello custom "${metadata.name}" non trovato su Firebase. Verrà creato un placeholder.`);
        }
      }
    }

    // Aggiorna il catalogo se sono stati aggiunti nuovi modelli
    if (modelsAdded && window.app && window.app.catalogPanel) {
      window.app.catalogPanel.renderCatalog();
      console.log('✓ Catalogo aggiornato con modelli custom caricati');
    }
  }

  newProject() {
    this.clearScene();
    this.currentProjectId   = null;
    this.currentProjectName = null;

    // Aggiorna il nome progetto nella toolbar
    if (window.app?.toolbar) {
      window.app.toolbar.updateButtons();
    }

    console.log('✓ Scena azzerata per nuovo progetto');
  }

  clearScene() {
    const furnitureController = this.engine.getFurnitureController();
    const wallController = this.engine.getWallController();
    const doorController = this.engine.getDoorController();
    const windowController = this.engine.getWindowController();

    furnitureController.dispose();
    doorController.clearDoors();
    windowController.clearWindows();
    wallController.clearWalls();

    window.app?.engine?.getSelectionController()?.deselectAll();
    if (window.app) {
      window.app.setLocked(false);
      window.app.updateAllButtons();
    }
    window.requestRender?.();
  }

  // ========== OPERAZIONI FIREBASE ==========

  async saveProject(projectName) {
    const projectData = this.serializeProject();

    try {
      // Upload modelli custom su Firebase Storage se necessario
      await this.uploadCustomModelsToStorage(projectData.customModelsMetadata);

      // Salva progetto su Firestore
      const projectId = await this.firebaseService.saveProject({
        name: projectName,
        data: projectData,
        customModelsMetadata: projectData.customModelsMetadata
      });

      this.currentProjectId = projectId;
      this.currentProjectName = projectName;

      console.log('✓ Progetto salvato con successo:', projectName);
      return projectId;
    } catch (error) {
      console.error('Errore salvataggio progetto:', error);
      throw error;
    }
  }

  async updateProject() {
    if (!this.currentProjectId) {
      throw new Error('Nessun progetto caricato da aggiornare');
    }

    const projectData = this.serializeProject();

    try {
      // Upload eventuali nuovi modelli custom
      await this.uploadCustomModelsToStorage(projectData.customModelsMetadata);

      // Aggiorna progetto su Firestore
      await this.firebaseService.updateProject(this.currentProjectId, {
        name: this.currentProjectName,
        data: projectData,
        customModelsMetadata: projectData.customModelsMetadata
      });

      console.log('✓ Progetto aggiornato:', this.currentProjectName);
    } catch (error) {
      console.error('Errore aggiornamento progetto:', error);
      throw error;
    }
  }

  async loadProject(projectId) {
    try {
      const project = await this.firebaseService.loadProject(projectId);

      await this.deserializeProject(project.data);

      this.currentProjectId = project.id;
      this.currentProjectName = project.name;

      console.log('✓ Progetto caricato:', project.name);
      return project;
    } catch (error) {
      console.error('Errore caricamento progetto:', error);
      throw error;
    }
  }

  async getAllProjects() {
    try {
      return await this.firebaseService.getAllProjects();
    } catch (error) {
      console.error('Errore caricamento lista progetti:', error);
      throw error;
    }
  }

  async deleteProject(projectId) {
    try {
      await this.firebaseService.deleteProject(projectId);
      
      if (this.currentProjectId === projectId) {
        this.currentProjectId = null;
        this.currentProjectName = null;
      }

      console.log('✓ Progetto eliminato');
    } catch (error) {
      console.error('Errore eliminazione progetto:', error);
      throw error;
    }
  }

  async uploadCustomModelsToStorage(customModelsMetadata) {
    const furnitureController = this.engine.getFurnitureController();
    const customModelLoader = furnitureController.customModelLoader;

    if (!customModelLoader) return;

    for (const metadata of customModelsMetadata) {
      // Verifica se il modello esiste già su Storage
      const exists = await this.firebaseService.customModelExists(metadata.id);
      
      if (!exists) {
        // Trova il modello locale
        const localModel = customModelLoader.getCustomModels().find(m => m.id === metadata.id);
        
        if (localModel && localModel.modelData) {
          // Converte ArrayBuffer in Blob
          const blob = new Blob([localModel.modelData], { type: 'model/gltf-binary' });
          
          // Upload su Firebase Storage
          await this.firebaseService.uploadCustomModel(blob, metadata.id);
          console.log('✓ Modello custom caricato su Firebase Storage:', metadata.name);
        }
      }
    }
  }

  getCurrentProjectName() {
    return this.currentProjectName;
  }

  hasCurrentProject() {
    return this.currentProjectId !== null;
  }
}

// Import necessari
import * as THREE from 'three';
import Wall from '../models/wall.js';
import Door from '../models/door.js';
import Window from '../models/window.js';

export default ProjectManager;